/**
 * SortableSidebarItem — @dnd-kit sortable wrapper for sidebar items.
 *
 * Wraps children with useSortable. Provides drag handle via CSS
 * cursor, opacity feedback during drag, and layout animation.
 *
 * The actual item content (SidebarItem) is passed as children —
 * this component only adds the DnD behavior layer.
 */

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { ReactNode } from "react"
import { cn } from "@/shared/utils/general/cn"
import { useSidebarDragBlock } from "./SidebarDndContext"

interface SortableSidebarItemProps {
  /** Unique ID for this sortable item (same as the menuItem.id) */
  id: string
  /** The sidebar item content */
  children: ReactNode
  /** Whether this item is disabled from being dragged */
  disabled?: boolean
  /** Additional class names (e.g. mt-5 for area spacing) */
  className?: string
}

export function SortableSidebarItem({
  id,
  children,
  disabled,
  className,
}: SortableSidebarItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
  })

  // Dim items that are part of a dragged area block (companions)
  const dragBlock = useSidebarDragBlock()
  const isCompanion = dragBlock != null && dragBlock.has(id)

  const style = {
    // Only apply scaleX/scaleY transform — prevent layout thrashing from
    // translate values that @dnd-kit adds during sort animation.
    transform: CSS.Transform.toString(
      transform ? { ...transform, scaleX: 1, scaleY: 1 } : null,
    ),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "relative",
        className,
        isDragging && "z-50 opacity-50",
        isCompanion && !isDragging && "opacity-40 transition-opacity",
      )}
    >
      {children}
    </li>
  )
}
